import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, UserPlus, Loader2, Trash2, Plus, X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_MAP = {
  lunes: { label: 'Lun', color: 'bg-blue-100 text-blue-700' },
  martes: { label: 'Mar', color: 'bg-violet-100 text-violet-700' },
  miercoles: { label: 'Mie', color: 'bg-emerald-100 text-emerald-700' },
  jueves: { label: 'Jue', color: 'bg-amber-100 text-amber-700' },
  viernes: { label: 'Vie', color: 'bg-red-100 text-red-700' },
  sabado: { label: 'Sab', color: 'bg-pink-100 text-pink-700' },
  domingo: { label: 'Dom', color: 'bg-orange-100 text-orange-700' },
};

const AssignModal = ({ diet, onClose, onAssigned }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    axios.get(`${API}/diet/distributor/my-clients`)
      .then(res => setClients(res.data))
      .catch(() => toast.error('Error al cargar clientes'))
      .finally(() => setLoading(false));
  }, []);

  const handleAssign = async (clientId, clientName) => {
    setAssigning(clientId);
    try {
      await axios.post(`${API}/diet/distributor/assign/${diet.id}`, { client_id: clientId });
      toast.success(`Dieta asignada a ${clientName}`);
      onAssigned();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    }
    setAssigning(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="assign-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Asignar Dieta</h3>
            <p className="text-xs text-gray-500 mt-0.5">{diet.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#467322]" size={24} /></div>
        ) : clients.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No tienes clientes registrados</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Selecciona un cliente</p>
            {clients.map(client => (
              <button key={client.id} onClick={() => handleAssign(client.id, client.name)}
                disabled={assigning === client.id}
                className="w-full flex items-center justify-between p-3.5 rounded-xl border border-gray-100 hover:border-[#467322] hover:bg-green-50/30 transition-all group"
                data-testid={`assign-client-${client.id}`}>
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-900">{client.name}</p>
                  <p className="text-xs text-gray-400">{client.email}</p>
                </div>
                {assigning === client.id ? (
                  <Loader2 size={16} className="animate-spin text-[#467322]" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#467322] flex items-center justify-center transition-colors">
                    <Check size={14} className="text-gray-300 group-hover:text-white transition-colors" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DistributorSavedDietsPage = () => {
  const navigate = useNavigate();
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [assignDiet, setAssignDiet] = useState(null);

  const fetchDiets = useCallback(() => {
    axios.get(`${API}/diet/distributor/custom-list`)
      .then(res => setDiets(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDiets(); }, [fetchDiets]);

  const handleDelete = async (dietId) => {
    setDeletingId(dietId);
    try {
      await axios.delete(`${API}/diet/distributor/custom/${dietId}`);
      toast.success('Dieta eliminada');
      setDiets(prev => prev.filter(d => d.id !== dietId));
    } catch {
      toast.error('Error al eliminar');
    }
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="dist-saved-diets-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/distributor')}
            className="flex items-center gap-2 text-[#467322] font-semibold text-sm hover:text-[#1A2E10] transition-colors"
            data-testid="back-to-profile">
            <ArrowLeft size={18} /> Inicio
          </button>
        </div>
        <button onClick={() => navigate('/distributor/profile/diet-configurator')}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors shadow-sm"
          data-testid="new-diet-btn">
          <Plus size={15} /> Nueva Dieta
        </button>
      </div>

      <h1 className="text-2xl font-black text-[#1A2E10] mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mis Dietas Guardadas</h1>
      <p className="text-sm text-gray-500 mb-6">{diets.length} dieta{diets.length !== 1 ? 's' : ''} creada{diets.length !== 1 ? 's' : ''}</p>

      {diets.length === 0 ? (
        <div className="text-center py-16">
          <Calendar size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 font-semibold mb-2">No tienes dietas guardadas</p>
          <p className="text-sm text-gray-400 mb-6">Crea tu primera dieta con el configurador</p>
          <button onClick={() => navigate('/distributor/profile/diet-configurator')}
            className="px-6 py-3 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors">
            Crear Mi Primera Dieta
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {diets.map(diet => (
            <div key={diet.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`dist-diet-${diet.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{diet.name}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {diet.selected_days?.map(day => (
                      <span key={day} className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${DAYS_MAP[day]?.color || 'bg-gray-100 text-gray-600'}`}>
                        {DAYS_MAP[day]?.label || day}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>{diet.selected_meals?.length || 0} comidas/dia</span>
                    <span>{new Date(diet.created_at).toLocaleDateString('es')}</span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 mt-4">
                <button onClick={() => navigate(`/distributor/profile/diets/preview/${diet.id}`)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold hover:bg-gray-200 transition-colors"
                  data-testid={`view-diet-${diet.id}`}>
                  <Eye size={14} /> Ver Dieta
                </button>
                <button onClick={() => setAssignDiet(diet)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors"
                  data-testid={`assign-diet-${diet.id}`}>
                  <UserPlus size={14} /> Asignar
                </button>
                <button onClick={() => setConfirmDeleteId(diet.id)}
                  disabled={deletingId === diet.id}
                  className="flex items-center justify-center w-10 h-10 rounded-xl border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                  data-testid={`delete-diet-${diet.id}`}>
                  {deletingId === diet.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <h3 className="text-lg font-black text-center text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Eliminar Dieta</h3>
            <p className="text-sm text-gray-500 text-center mt-2">Esta accion no se puede deshacer.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50"
                data-testid="delete-cancel-btn">Cancelar</button>
              <button onClick={() => handleDelete(confirmDeleteId)} disabled={deletingId === confirmDeleteId}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 disabled:opacity-50"
                data-testid="delete-confirm-btn">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignDiet && (
        <AssignModal diet={assignDiet} onClose={() => setAssignDiet(null)} onAssigned={fetchDiets} />
      )}
    </div>
  );
};

export default DistributorSavedDietsPage;
